import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import authMonitorLite from "../services/auth-monitor-lite";
import logger from "../helpers/logger";

/**
 * Lightweight Authentication Hook
 * Minimal overhead, only checks when needed
 */
export const useAuthLite = () => {
  const { instance, accounts } = useMsal();
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize once
  useEffect(() => {
    if (instance && accounts && accounts.length > 0 && !isInitialized) {
      logger.log("🔐 Initializing lightweight auth hook");
      const currentAccount = accounts[0];
      authMonitorLite.initialize(instance, currentAccount);
      setIsInitialized(true);
    }
  }, [instance, accounts, isInitialized]);

  // Simple validation function for critical operations
  const validateAuth = useCallback(async () => {
    if (!isInitialized) {
      logger.warn("Auth not initialized");
      return false;
    }

    return await authMonitorLite.validateBeforeOperation();
  }, [isInitialized]);

  // Manual token refresh
  const refreshAuth = useCallback(async () => {
    if (!isInitialized) return false;
    return await authMonitorLite.refreshToken();
  }, [isInitialized]);

  // Get current status (cached, won't block)
  const getAuthStatus = useCallback(async () => {
    if (!isInitialized) {
      return { status: "initializing", message: "Initializing..." };
    }

    return await authMonitorLite.checkAuthStatus();
  }, [isInitialized]);

  return {
    isInitialized,
    validateAuth, // Use this before critical operations
    refreshAuth, // Manual refresh
    getAuthStatus, // Get status when needed
    isReady: isInitialized,
  };
};
